import type { DashboardChartControls } from './dashboardConfig';

// Apply the live toolbar transforms to a line chart's rows. Order matters: accumulate first, so
// a percentage reads against the running totals rather than the daily deltas — the same order
// the summary reporting controls use.
export const applyLineControls = (
    rows: Record<string, number | string>[],
    seriesKeys: string[],
    expectedKey: string | undefined,
    controls: DashboardChartControls,
): Record<string, number | string>[] => {
    if (!controls.cumulative && !(controls.showPercent && expectedKey)) return rows;
    // The expected series is carried through the maths even when it is filtered out of the
    // chart, because the percentage is computed against it.
    const keys =
        expectedKey && !seriesKeys.includes(expectedKey)
            ? [...seriesKeys, expectedKey]
            : seriesKeys;
    let out = rows.map(row => ({ ...row }));

    if (controls.cumulative) {
        const totals: Record<string, number> = {};
        out = out.map(row => {
            const next = { ...row };
            keys.forEach(key => {
                totals[key] = (totals[key] ?? 0) + Number(row[key] ?? 0);
                next[key] = totals[key];
            });
            return next;
        });
    }

    if (controls.showPercent && expectedKey) {
        out = out.map(row => {
            const expected = Number(row[expectedKey] ?? 0);
            const next = { ...row };
            keys.filter(key => key !== expectedKey).forEach(key => {
                next[key] = expected > 0 ? (Number(row[key] ?? 0) / expected) * 100 : 0;
            });
            return next;
        });
    }

    return out;
};
export const formatCompact = (value: number): string => {
    if (!Number.isFinite(value)) return '—';
    const abs = Math.abs(value);
    if (abs >= 1_000_000_000) return `${(value / 1_000_000_000).toFixed(2)}B`;
    if (abs >= 1_000_000) return `${(value / 1_000_000).toFixed(2)}M`;
    if (abs >= 1_000) return `${(value / 1_000).toFixed(1)}K`;
    return Math.round(value).toLocaleString();
};

// Axis ticks want the shortest label that is still unambiguous: "900M", "1.2B". The two
// decimals formatCompact keeps — good in a tooltip — make an axis label wide enough that the
// chart clips it. Trailing zeros are dropped, so 1.20B reads 1.2B and 900.00M reads 900M.
export const formatAxisCompact = (value: number): string => {
    if (!Number.isFinite(value)) return '—';
    const abs = Math.abs(value);
    const trim = (n: number) => String(Number(n.toFixed(1)));
    if (abs >= 1_000_000_000) return `${trim(value / 1_000_000_000)}B`;
    if (abs >= 1_000_000) return `${trim(value / 1_000_000)}M`;
    if (abs >= 1_000) return `${trim(value / 1_000)}K`;
    return Math.round(value).toLocaleString();
};

// Wrap long category labels across lines for the axis-less "ranked bar" style.
export const splitLabelIntoLines = (raw: string, maxLen = 17): string[] => {
    if (!raw || raw.length <= maxLen) return [raw];
    const words = raw.replace(/([a-z0-9])([A-Z])/g, '$1 $2').split(/\s+/);
    const lines: string[] = [];
    let current = '';
    words.forEach(word => {
        if (!current) current = word;
        else if (`${current} ${word}`.length <= maxLen) current = `${current} ${word}`;
        else {
            lines.push(current);
            current = word;
        }
    });
    if (current) lines.push(current);
    return lines;
};

export type MiniBar = { label: string; value: number };

// The top `cap` bars plus one "Others" bar for the rest: their sum, or their rounded average
// for a percentage chart. Left off when the rest add up to nothing.
export const withOthers = (data: MiniBar[], mode: 'sum' | 'avg' = 'sum', cap = 5): MiniBar[] => {
    if (data.length <= cap) return data;
    const top = data.slice(0, cap);
    const rest = data.slice(cap);
    const othersValue =
        mode === 'avg'
            ? Math.round(rest.reduce((s, d) => s + d.value, 0) / rest.length)
            : rest.reduce((s, d) => s + d.value, 0);
    if (othersValue <= 0) return top;
    return [...top, { label: 'Others', value: othersValue }];
};
